<template>
  <a-layout-sider
    collapsible
    class="sider-primary dashboard-sider"
    breakpoint="lg"
    :collapsed-width="collapsedWidth"
    width="256px"
    :collapsed="sidebarCollapsed"
    @collapse="onCollapse"
    @breakpoint="onBreakpoint"
    :trigger="null"
    :class="[
      'ant-layout-sider-' + sidebarColorVal,
      'ant-layout-sider-' + sidebarThemeVal,
      {
        'dashboard-sider--collapsed': sidebarCollapsed,
        'dashboard-sider--mini': isMiniMode,
        'dashboard-sider--hidden': isHiddenMode,
      },
    ]"
    theme="light"
    :style="{ backgroundColor: 'transparent' }"
  >
    <div class="dashboard-sider__inner">
      <div class="dashboard-sider__decor" aria-hidden="true" />

      <div class="dashboard-sider__brand" @click="goHome">
        <template v-if="isMiniMode">
          <img
            v-if="companyLogo"
            :src="companyLogo"
            :alt="companyName"
            class="dashboard-sider__brand-img"
          />
          <span v-else class="dashboard-sider__brand-mark">{{ companyInitial }}</span>
        </template>
        <div v-else class="dashboard-sider__brand-text">
          <span class="dashboard-sider__brand-title">{{ companyName }}</span>
          <span class="dashboard-sider__brand-tagline">{{ companyTagline }}</span>
        </div>
      </div>

      <hr class="dashboard-sider__divider" />

      <div class="dashboard-sider__content">
        <a-menu
          mode="inline"
          class="dashboard-sidebar-menu"
          v-model:openKeys="openKeys"
          v-model:selectedKeys="selectedKeys"
          :inline-collapsed="isMiniMode"
          :inline-indent="16"
        >
          <div v-for="item in menuList" :key="item.id">
            <a-sub-menu v-if="item.children?.length" :key="menuItemKey(item)">
              <template #title>
                <DashboardMenuItem :item="item" :icon-only="isMiniMode" />
              </template>

              <div v-for="child in item.children" :key="child.id">
                <a-sub-menu
                  v-if="child.children?.length"
                  :key="menuItemKey(child)"
                >
                  <template #title>
                    <DashboardMenuItem :item="child" :show-icon="false" />
                  </template>
                  <a-menu-item
                    v-for="sub in child.children"
                    :key="menuItemKey(sub)"
                    @click="onMenuClick(sub)"
                  >
                    <DashboardMenuItem :item="sub" :show-icon="false" />
                  </a-menu-item>
                </a-sub-menu>

                <a-menu-item
                  v-else
                  :key="menuItemKey(child)"
                  @click="onMenuClick(child)"
                >
                  <DashboardMenuItem :item="child" :show-icon="false" />
                </a-menu-item>
              </div>
            </a-sub-menu>

            <a-menu-item v-else :key="menuItemKey(item)" @click="onMenuClick(item)">
              <DashboardMenuItem :item="item" :icon-only="isMiniMode" />
            </a-menu-item>
          </div>
        </a-menu>
      </div>

      <button type="button" class="dashboard-sider__back" @click="onBack">
        <i class="fa-solid fa-arrow-right-from-bracket dashboard-sider__back-icon" />
        <span v-if="!isMiniMode" class="dashboard-sider__back-label">{{ backLabel }}</span>
      </button>
    </div>
  </a-layout-sider>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { apiView } from "@/api/index";
import { useRouter } from "vue-router";
import utils from "@/utils/Utils";
import commons from "@/utils/Common";
import DashboardMenuItem from "@/components/Sidebars/DashboardMenuItem.vue";

const emit = defineEmits(["toggleSidebar"]);

const LG_BREAKPOINT = 992;
const MINI_WIDTH = 72;

interface SlideMenuItem {
  id: number;
  parent: number;
  linkTo: string;
  linkLabel: string;
  icon?: string;
  children?: SlideMenuItem[];
}

interface Props {
  sidebarCollapsed: boolean;
  sidebarColor: string;
  sidebarTheme: string;
  backLabel?: string;
  backUrl?: string;
}

const propsData = withDefaults(defineProps<Props>(), {
  sidebarCollapsed: false,
  sidebarColor: "primary",
  sidebarTheme: "light",
  backLabel: "Quay lại Trang chủ",
  backUrl: "",
});

const sidebarColorVal = computed(() => propsData.sidebarColor ?? "primary");
const sidebarThemeVal = computed(() => propsData.sidebarTheme ?? "light");

const router = useRouter();
const menuList = ref<SlideMenuItem[]>([]);
const openKeys = ref<string[]>([]);
const selectedKeys = ref<string[]>([]);
const isMobile = ref(false);
const company = ref<Record<string, string>>({});

const activePath = computed(() => router.currentRoute.value.path);
const isMiniMode = computed(() => propsData.sidebarCollapsed && !isMobile.value);
const isHiddenMode = computed(() => propsData.sidebarCollapsed && isMobile.value);
const collapsedWidth = computed(() => (isMobile.value ? 0 : MINI_WIDTH));
const companyName = computed(
  () => company.value.sub_name || company.value.subName || "KIMAHOME"
);
const companyTagline = computed(
  () => company.value.summary || "LUXURY FURNITURE"
);
const companyLogo = computed(
  () => company.value.logo || company.value.urlLogo || ""
);
const companyInitial = computed(() => companyName.value.charAt(0).toUpperCase());

const menuItemKey = (item: SlideMenuItem) =>
  item.linkTo?.trim() ? item.linkTo : `menu-${item.id}`;

const updateViewport = () => {
  isMobile.value = window.innerWidth < LG_BREAKPOINT;
};

const normalizeFlatMenu = (res: any): SlideMenuItem[] => {
  const payload = res?.data?.data ?? res?.data;
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.body)) {
    return payload.body;
  }
  if (Array.isArray(res?.data?.body)) {
    return res.data.body;
  }
  const parsed = utils.parseJSON(payload);
  if (Array.isArray(parsed)) {
    return parsed;
  }
  if (Array.isArray(parsed?.body)) {
    return parsed.body;
  }
  return [];
};

const buildMenuTree = (flat: SlideMenuItem[]): SlideMenuItem[] => {
  const map = new Map<number, SlideMenuItem>();

  flat.forEach((item) => {
    map.set(item.id, { ...item, children: [] });
  });

  const roots: SlideMenuItem[] = [];

  flat.forEach((item) => {
    const node = map.get(item.id);
    if (!node) {
      return;
    }
    const parentId = Number(item.parent) || 0;
    if (parentId === 0) {
      roots.push(node);
      return;
    }
    const parent = map.get(parentId);
    if (parent?.children) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const trimEmptyChildren = (nodes: SlideMenuItem[]) => {
    nodes.sort((a, b) => a.id - b.id);
    nodes.forEach((node) => {
      if (node.children?.length) {
        trimEmptyChildren(node.children);
      } else {
        delete node.children;
      }
    });
  };

  trimEmptyChildren(roots);
  return roots;
};

const findOpenKeysForPath = (
  items: SlideMenuItem[],
  path: string,
  parents: string[] = []
): string[] | null => {
  for (const item of items) {
    if (item.linkTo === path) {
      return parents;
    }
    if (item.children?.length) {
      const found = findOpenKeysForPath(item.children, path, [
        ...parents,
        menuItemKey(item),
      ]);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
};

const syncMenuState = () => {
  const path = activePath.value;
  const matchKey =
    findMenuKeyByPath(menuList.value, path) ?? path;
  selectedKeys.value = [matchKey];

  const keys = findOpenKeysForPath(menuList.value, path);
  if (keys !== null) {
    openKeys.value = keys;
  }
};

const findMenuKeyByPath = (
  items: SlideMenuItem[],
  path: string
): string | null => {
  for (const item of items) {
    if (item.linkTo === path) {
      return menuItemKey(item);
    }
    if (item.children?.length) {
      const found = findMenuKeyByPath(item.children, path);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const loadData = async () => {
  const res: any = await apiView("admin-slide-menu");
  const flat = normalizeFlatMenu(res);
  menuList.value = buildMenuTree(flat);
  syncMenuState();
};

const loadCompany = () => {
  const data = commons.getCokiesCompany();
  if (data) {
    company.value = data;
  }
};

const onMenuClick = (item: SlideMenuItem) => {
  if (item.linkTo?.trim()) {
    router.push(item.linkTo);
  }
};

const onCollapse = (collapsed: boolean) => {
  emit("toggleSidebar", collapsed);
};

const onBreakpoint = (broken: boolean) => {
  isMobile.value = broken;
  emit("toggleSidebar", broken);
};

const goHome = () => {
  router.push("/");
};

const onBack = () => {
  const url = propsData.backUrl?.trim();
  if (url) {
    window.location.href = url;
    return;
  }
  goHome();
};

onMounted(async () => {
  updateViewport();
  loadCompany();
  await loadData();
  window.addEventListener("resize", updateViewport);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateViewport);
});

watch(activePath, () => {
  syncMenuState();
});

watch(menuList, () => syncMenuState(), { deep: true });

watch(isMiniMode, (mini) => {
  if (mini) {
    openKeys.value = [];
  } else {
    syncMenuState();
  }
});
</script>

<style lang="scss" scoped>
$mini-width: 72px;

.dashboard-sider {
  position: relative;
  overflow: hidden !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: 100vh !important;
  border-radius: 0 !important;
  transition: all 0.2s ease;

  &.dashboard-sider--hidden {
    flex: 0 0 0 !important;
    max-width: 0 !important;
    min-width: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
    border: none !important;
    pointer-events: none;
  }

  &.dashboard-sider--mini {
    flex: 0 0 $mini-width !important;
    max-width: $mini-width !important;
    min-width: $mini-width !important;
    width: $mini-width !important;
    pointer-events: auto;
  }
}

.dashboard-sider :deep(.ant-layout-sider-children) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 256px;
  transition: width 0.2s ease;
}

.dashboard-sider.dashboard-sider--mini :deep(.ant-layout-sider-children) {
  width: $mini-width !important;
}

.dashboard-sider.dashboard-sider--hidden :deep(.ant-layout-sider-children) {
  width: 0 !important;
  overflow: hidden !important;
  opacity: 0;
}

.dashboard-sider__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  padding: 20px 12px 16px;
  background: linear-gradient(
    165deg,
    #0a1829 0%,
    var(--primary-dark, #102542) 28%,
    #16365a 52%,
    #1a4268 72%,
    var(--primary-dark, #102542) 100%
  );
  border-right: 1px solid rgba(177, 150, 99, 0.2);
  color: var(--default-header-color, var(--primary-gold, #b19663));
  overflow: hidden;
}

.dashboard-sider__decor {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  background:
    linear-gradient(
      145deg,
      transparent 0%,
      transparent 32%,
      rgba(26, 66, 104, 0.55) 42%,
      rgba(177, 150, 99, 0.1) 55%,
      rgba(10, 24, 41, 0.45) 68%,
      transparent 78%
    ),
    radial-gradient(
      ellipse 120% 80% at 0% 100%,
      rgba(22, 54, 90, 0.65) 0%,
      transparent 55%
    );
}

.dashboard-sider--mini .dashboard-sider__inner {
  align-items: center;
  padding: 16px 8px 12px;
}

.dashboard-sider__brand {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0 0 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.dashboard-sider--mini .dashboard-sider__brand {
  width: 44px;
  height: 44px;
  margin: 0 auto 12px;
}

.dashboard-sider__brand-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 4px 4px 8px;
  text-align: center;
}

.dashboard-sider__brand-title {
  font-family: var(--heading-font, "libre-baskerville", serif);
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-white, #fff);
  white-space: nowrap;
}

.dashboard-sider__brand-tagline {
  margin-top: 6px;
  font-family: var(--nav-font, "libre-baskerville", serif);
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--default-header-color, var(--primary-gold, #b19663));
  white-space: nowrap;
}

.dashboard-sider__divider {
  position: relative;
  z-index: 1;
  width: 100%;
  margin: 8px 0 12px;
  border: none;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(177, 150, 99, 0.55) 20%,
    rgba(177, 150, 99, 0.55) 80%,
    transparent 100%
  );
  flex-shrink: 0;
}

.dashboard-sider--mini .dashboard-sider__divider {
  width: 28px;
  margin: 0 auto 12px;
}

.dashboard-sider__brand-img {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.dashboard-sider__brand-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    var(--primary-gold-light, #f4d03f) 0%,
    var(--default-header-color, #b19663) 100%
  );
  color: var(--primary-dark, #102542);
  font-size: 16px;
  font-weight: 700;
}

.dashboard-sider__content {
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
}

.dashboard-sider__back {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(177, 150, 99, 0.45);
  border-radius: 8px;
  background: transparent;
  color: var(--default-header-color, var(--primary-gold, #b19663));
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(177, 150, 99, 0.12);
    border-color: var(--primary-gold, #d4af37);
  }
}

.dashboard-sider--mini .dashboard-sider__back {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 10px;
}

.dashboard-sider__back-icon {
  font-size: 14px;
}

.dashboard-sider :deep(.dashboard-sidebar-menu) {
  border-inline-end: none !important;
  background: transparent !important;
  width: 100% !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title) {
  min-height: 40px;
  line-height: 1.4;
  margin: 1px 0 !important;
  padding: 8px 10px !important;
  color: var(--default-header-color, var(--primary-gold, #b19663));
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  width: calc(100% - 4px);
  transition: background-color 0.15s ease, color 0.15s ease;

  &::after {
    display: none;
  }
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title) {
  padding-inline-end: 32px !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .dashboard-menu-item__icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  flex-shrink: 0;
  margin-inline-end: 8px;
  font-size: 15px;
  color: var(--default-header-color, var(--primary-gold, #b19663));
  opacity: 0.9;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .dashboard-menu-item__label) {
  vertical-align: middle;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-arrow) {
  color: rgba(177, 150, 99, 0.7);
  inset-inline-end: 10px;
  font-size: 10px;
}

.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-submenu),
.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  margin-bottom: 2px;
  border-bottom: 1px dashed rgba(177, 150, 99, 0.28);
  border-radius: 0;
}

.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-submenu > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  border-radius: 0;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-sub) {
  background: transparent !important;

  .ant-menu-item {
    min-height: 36px;
    margin: 1px 0 !important;
    padding: 6px 10px 6px 12px !important;
    font-size: 13px;
    font-weight: 400;
    color: rgba(177, 150, 99, 0.78);
    border-radius: 8px;
  }
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item:hover),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-title:hover) {
  color: var(--primary-gold-light, #f4d03f);
  background: rgba(177, 150, 99, 0.12) !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-item-selected) {
  color: var(--text-white, #fff) !important;
  font-weight: 600;
  background: rgba(177, 150, 99, 0.16) !important;
  box-shadow: inset 3px 0 0 var(--default-header-color, var(--primary-gold, #b19663));
}

.dashboard-sider
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__icon) {
  color: var(--primary-gold-light, #f4d03f);
  opacity: 1;
}

.dashboard-sider
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__label) {
  color: var(--text-white, #fff);
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title),
.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title) {
  color: var(--primary-gold-light, #f4d03f);
  background: transparent !important;
}

.dashboard-sider :deep(.dashboard-sidebar-menu .ant-menu-submenu-open > .ant-menu-submenu-title) {
  font-weight: 500;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-submenu),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-item) {
  border-bottom: none;
}

/* Mini mode: icon only */
.dashboard-sider--mini :deep(.dashboard-sidebar-menu) {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-item),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu > .ant-menu-submenu > .ant-menu-submenu-title) {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  margin: 4px auto !important;
  padding: 0 !important;
  border-radius: 10px;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu .dashboard-menu-item) {
  justify-content: center;
  width: 100%;
  gap: 0;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu .dashboard-menu-item__icon) {
  width: auto;
  margin: 0;
  font-size: 18px;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu .dashboard-menu-item__label),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu .dashboard-menu-item__badge),
.dashboard-sider--mini :deep(.dashboard-sidebar-menu .ant-menu-submenu-arrow) {
  display: none !important;
}

.dashboard-sider--mini :deep(.dashboard-sidebar-menu .ant-menu-item-selected),
.dashboard-sider--mini
  :deep(.dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title) {
  background: rgba(177, 150, 99, 0.2) !important;
  box-shadow: none;
}

.dashboard-sider--mini
  :deep(.dashboard-sidebar-menu .ant-menu-item-selected .dashboard-menu-item__icon),
.dashboard-sider--mini
  :deep(
    .dashboard-sidebar-menu .ant-menu-submenu-selected > .ant-menu-submenu-title .dashboard-menu-item__icon
  ) {
  color: var(--primary-gold-light, #f4d03f);
}
</style>
